import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { MakeCountryMapping } from 'api/types';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import config from '../config';

async function getMakeCountryMapping({
  axios,
  selectedMakeOptions,
  selectedCountriesOptions,
  selectedFormOptions,
}: {
  axios: AxiosInstance;
  selectedMakeOptions: number[];
  selectedCountriesOptions: string[];
  selectedFormOptions: number[];
}): Promise<MakeCountryMapping[]> {
  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/config/make-country-mapping`, {
    params: {
      make_ids: selectedMakeOptions.join(',') ?? null,
      countries: selectedCountriesOptions.join(',') ?? null,
      dsb_form_ids: selectedFormOptions.join(',') ?? null,
    },
  });

  return data;
}

export default function useMakeCountryMappingQuery({
  selectedMakeOptions,
  selectedCountriesOptions,
  selectedFormOptions,
}: {
  selectedMakeOptions: number[];
  selectedCountriesOptions: string[];
  selectedFormOptions: number[];
}) {
  const axios = useAxios();
  const toast = useToast();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey({ selectedMakeOptions, selectedCountriesOptions, selectedFormOptions }),
    queryFn: () =>
      getMakeCountryMapping({
        axios,
        selectedMakeOptions,
        selectedCountriesOptions,
        selectedFormOptions,
      }),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, JSON.stringify(query.error as any));
    }
  }, [query.error, query.isError, toast]);

  return query;
}

export function getKey({
  selectedMakeOptions,
  selectedCountriesOptions,
  selectedFormOptions,
}: {
  selectedMakeOptions: number[];
  selectedCountriesOptions: string[];
  selectedFormOptions: number[];
}) {
  return ['MakeCountryMapping', selectedMakeOptions, selectedCountriesOptions, selectedFormOptions] as const satisfies QueryKey;
}
useMakeCountryMappingQuery.getKey = getKey;
